// The interactive-MFA login `alert` packs CR's instruction (prompt) and an
// optional retry error into one string as "<prompt><SEP><error>" (PROA-1534).
// Keep this separator in sync with the pipeline (_MFA_ALERT_SEPARATOR in
// ehr_providers/domain/centralreach/actions.py). It's the ASCII Unit Separator
// (U+001F) — invisible and won't occur in real copy. Built via fromCharCode so no
// control character lives literally in source.
export const MFA_ALERT_SEPARATOR = String.fromCharCode(0x1f);

// The separator can reach the portal in more than one representation depending on
// how it is encoded/transported through FDM/GraphQL: the printable "symbol for unit
// separator" glyph (U+241F) or the escaped 6-character literal (backslash-u-001f).
// We normalise those to the canonical separator before splitting.
const MFA_ALERT_SEPARATOR_SYMBOL = String.fromCharCode(0x241f);
const MFA_ALERT_ESCAPED_LITERALS = ["\\u001f", "\\u001F"];

const replaceAll = (input: string, search: string, replacement: string): string =>
  input.split(search).join(replacement);

/**
 * Split an MFA login alert into its prompt (CR's instruction) and an optional
 * error (e.g. "Invalid MFA code. Please re-enter." on a retry). Any alternate
 * separator representation is normalised first, and segments are trimmed so
 * whitespace padding the separator doesn't reach the UI.
 */
export function splitMfaAlert(alert: string | null | undefined): {
  prompt: string | null;
  error: string | null;
} {
  if (!alert) return { prompt: null, error: null };
  let normalized = replaceAll(alert, MFA_ALERT_SEPARATOR_SYMBOL, MFA_ALERT_SEPARATOR);
  for (const literal of MFA_ALERT_ESCAPED_LITERALS) {
    normalized = replaceAll(normalized, literal, MFA_ALERT_SEPARATOR);
  }
  const [prompt, error] = normalized.split(MFA_ALERT_SEPARATOR);
  return { prompt: prompt?.trim() || null, error: error?.trim() || null };
}
